import { useState } from 'react'
import { LayoutChangeEvent, Platform, StyleSheet } from 'react-native'

import { MotiView } from '@zet/common/components/motiView'
import { Toast } from '@zet/design-system'
import { Stack } from '@zet/design-system/layout'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useFontFlowStore } from '../../../store/fontFlowStore'
import { useIsDarkMode } from '../../hooks'
import { PanToClose } from '../panToClose'
import JfsContainer from './JfsContainer'
import type { ToastProps } from './types'

export const SAFE_AREA_TOP = 20

export const ToastContent = ({
  render,
  intent,
  heading,
  description,
  hide,
  action,
  leftIcon,
}: ToastProps) => {
  const isDark = useIsDarkMode()
  const safeAreaInsets = useSafeAreaInsets()
  const [layout, setLayout] = useState<
    LayoutChangeEvent['nativeEvent']['layout'] | undefined
  >()
  const { isJfsFlow } = useFontFlowStore()

  const { top: safeAreaTop } =
    Platform.OS === 'web' ? { top: SAFE_AREA_TOP } : safeAreaInsets
  const toastHeight = layout?.height ?? 0

  return (
    <MotiView
      accessibilityLiveRegion="polite"
      pointerEvents="box-none"
      from={{ translateY: -toastHeight }}
      animate={{
        translateY: safeAreaTop === 0 ? SAFE_AREA_TOP : safeAreaTop,
      }}
      exit={{ translateY: -toastHeight }}
      transition={{ type: 'timing', duration: 350 }}
      onLayout={(e) => {
        setLayout(e.nativeEvent.layout)
      }}
    >
      <PanToClose panCloseDirection="up" closeDuration={500} onClose={hide}>
        <Stack className={`m-2 rounded-2xl shadow-md`} pointerEvents="box-none">
          {render ? (
            render
          ) : isJfsFlow ? (
            <JfsContainer
              messageDescription={description}
              messageHeading={heading}
              intent={intent}
              onClosePress={hide}
              action={action}
              leftIcon={leftIcon}
            />
          ) : (
            <Toast
              intent={intent}
              messageHeading={heading}
              messageDescription={description}
              onClosePress={hide}
              action={action}
              leftIcon={leftIcon}
            />
          )}
        </Stack>
      </PanToClose>
    </MotiView>
  )
}

const styles = StyleSheet.create({
  toastContainer: {
    alignSelf: 'center',
    borderRadius: 16,
    shadowOffset: {
      width: 0,
      height: 8,
    },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 5,
    justifyContent: 'center',
  },
})
